import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = '/api';

function SectionHeader({ num, title }) {
  return (
    <div className="sec-divider">
      <span className="sec-num">{num}</span>
      <div className="sec-line" />
      <span className="sec-title">{title}</span>
      <div className="sec-mark" />
    </div>
  );
}

function Badge({ label, color = "#111" }) {
  return (
    <span style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "0.5rem",
      letterSpacing: "0.12em",
      padding: "2px 7px",
      background: color,
      color: "#fff",
      textTransform: "uppercase",
      flexShrink: 0,
    }}>
      {label}
    </span>
  );
}

function Toggle({ checked, onChange, label, disabled }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1 }}>
      <div
        onClick={() => !disabled && onChange(!checked)}
        style={{
          width: 32, height: 16,
          background: checked ? "#111" : "#ddd",
          position: "relative", cursor: disabled ? "not-allowed" : "pointer",
          transition: "background 0.12s", flexShrink: 0,
        }}
      >
        <div style={{
          position: "absolute", top: 2, left: checked ? 18 : 2,
          width: 12, height: 12,
          background: checked ? "#CC0000" : "#fff",
          border: "1px solid rgba(0,0,0,0.2)",
          transition: "left 0.12s, background 0.12s",
        }} />
      </div>
      {label && (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.1em", color: "#555" }}>
          {label}
        </span>
      )}
    </label>
  );
}

function ChannelRow({ ch, onDelete, onUpdate, index }) {
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ channel_id: ch.channel_id || "", auto_summarize: ch.auto_summarize, auto_save_notion: ch.auto_save_notion || false });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/channels/${ch.id}`, {
        channel_id: editForm.channel_id.trim() || null,
        auto_summarize: editForm.auto_summarize,
        auto_save_notion: editForm.auto_save_notion,
      });
      onUpdate(res.data);
      setEditing(false);
      toast.success("CHANNEL UPDATED");
    } catch (e) {
      toast.error(e.response?.data?.detail || "UPDATE FAILED");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeletingId(ch.id);
    try {
      await axios.delete(`${API}/channels/${ch.id}`);
      onDelete(ch.id);
      toast.success("CHANNEL REMOVED");
    } catch { toast.error("DELETE FAILED"); }
    finally { setDeletingId(null); }
  };

  return (
    <>
      <div
        data-testid={`channel-item-${ch.id}`}
        className="anim-in"
        style={{
          display: "grid",
          gridTemplateColumns: "44px 1fr auto auto auto",
          borderBottom: "1px solid #e8e8e4",
          animationDelay: `${index * 0.04}s`,
          transition: "background 0.1s",
          alignItems: "center",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#F9F9F7"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
      >
        {/* Row num */}
        <div style={{ padding: "12px 10px 12px 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#CC0000", fontWeight: 700 }}>
          {String(index + 1).padStart(2, "0")}
        </div>
        {/* Info */}
        <div style={{ padding: "12px 12px", minWidth: 0 }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.7rem", fontWeight: 600, color: "#111", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {ch.name.toUpperCase()}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {ch.channel_id
              ? <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888" }}>{ch.channel_id}</span>
              : <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#CC0000" }}>NO CHANNEL ID SET</span>
            }
          </div>
        </div>
        {/* Status badges */}
        <div style={{ padding: "12px 8px", display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
          {ch.auto_summarize && ch.channel_id && <Badge label="AUTO" color="#111" />}
          {ch.auto_summarize && !ch.channel_id && <Badge label="NO ID" color="#CC0000" />}
          {ch.auto_save_notion && <Badge label="NOTION" color="#555" />}
          {ch.last_checked_at && (
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", color: "#bbb", letterSpacing: "0.06em" }}>
              {new Date(ch.last_checked_at).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" })}
            </span>
          )}
        </div>
        {/* Edit btn */}
        <div style={{ padding: "12px 4px" }}>
          <button
            data-testid={`edit-channel-${ch.id}`}
            onClick={() => setEditing(e => !e)}
            style={{
              background: editing ? "#111" : "transparent",
              border: "1px solid #ccc",
              cursor: "pointer", padding: "4px 10px",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.6rem", color: editing ? "#fff" : "#888",
              transition: "all 0.1s",
            }}
            onMouseEnter={e => { if (!editing) { e.currentTarget.style.borderColor = "#111"; e.currentTarget.style.color = "#111"; }}}
            onMouseLeave={e => { if (!editing) { e.currentTarget.style.borderColor = "#ccc"; e.currentTarget.style.color = "#888"; }}}
          >
            {editing ? "CLOSE" : "EDIT"}
          </button>
        </div>
        {/* Delete btn */}
        <div style={{ padding: "12px 16px 12px 4px" }}>
          <button
            data-testid={`delete-channel-${ch.id}`}
            onClick={handleDelete}
            disabled={!!deletingId}
            style={{
              background: "transparent", border: "1px solid #ddd", cursor: "pointer",
              padding: "4px 8px", fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.65rem", color: "#CC0000", transition: "all 0.1s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#CC0000"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#CC0000"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#CC0000"; e.currentTarget.style.borderColor = "#ddd"; }}
          >
            {deletingId ? <Loader2 size={11} className="animate-spin" /> : "✕"}
          </button>
        </div>
      </div>

      {/* Inline edit form */}
      {editing && (
        <div className="anim-in" style={{ background: "#F9F9F7", borderBottom: "1px solid #e8e8e4", padding: "16px 20px", borderLeft: "3px solid #CC0000" }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", letterSpacing: "0.18em", color: "#CC0000", marginBottom: 12 }}>
            CHANNEL AUTOMATION SETTINGS
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 12, alignItems: "end", flexWrap: "wrap" }} className="r-grid-1">
            <div>
              <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", letterSpacing: "0.16em", color: "#888", display: "block", marginBottom: 5 }}>
                YOUTUBE CHANNEL ID (UC...)
              </label>
              <input
                data-testid={`channel-id-input-${ch.id}`}
                type="text"
                value={editForm.channel_id}
                onChange={e => setEditForm(p => ({ ...p, channel_id: e.target.value }))}
                placeholder="UCxxxxxxxxxxxxxxxxxxxxxx"
                className="ind-input"
                style={{ fontSize: "0.7rem" }}
              />
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", color: "#bbb", marginTop: 4 }}>
                Find at: youtube.com/@channel → About → Share → Copy channel ID
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 4 }}>
              <Toggle
                checked={editForm.auto_summarize}
                onChange={v => setEditForm(p => ({ ...p, auto_summarize: v }))}
                label="AUTO-SUMMARIZE"
              />
              <Toggle
                checked={editForm.auto_save_notion}
                onChange={v => setEditForm(p => ({ ...p, auto_save_notion: v }))}
                label="AUTO-NOTION"
              />
            </div>
            <button
              data-testid={`save-channel-edit-${ch.id}`}
              onClick={handleSaveEdit}
              disabled={saving}
              className="ind-btn-primary"
              style={{ padding: "8px 16px", alignSelf: "end" }}
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : "SAVE"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function ChannelsPage() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", handle: "", channel_id: "", auto_summarize: false, notion_database_id: "" });
  const [showForm, setShowForm] = useState(false);
  const [notionDbs, setNotionDbs] = useState([]);

  useEffect(() => { fetchChannels(); axios.get(API + "/settings").then(r => setNotionDbs(r.data.notion_databases || [])).catch(() => {}); }, []);

  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${API}/channels`);
      setChannels(res.data);
    } catch { toast.error("FAILED TO LOAD CHANNELS"); }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) { toast.error("NAME AND URL REQUIRED"); return; }
    setAdding(true);
    try {
      const res = await axios.post(`${API}/channels`, {
        name: form.name.trim(), url: form.url.trim(),
        handle: form.handle.trim() || null,
        channel_id: form.channel_id.trim() || null,
        auto_summarize: form.auto_summarize,
        notion_database_id: form.notion_database_id || null,
      });
      setChannels(p => [...p, res.data]);
      setForm({ name: "", url: "", handle: "", channel_id: "", auto_summarize: false, notion_database_id: "" });
      setShowForm(false);
      toast.success("CHANNEL ADDED");
    } catch (e) { toast.error(e.response?.data?.detail || "FAILED"); }
    finally { setAdding(false); }
  };

  const handleDelete = useCallback((id) => setChannels(p => p.filter(c => c.id !== id)), []);
  const handleUpdate = useCallback((updated) => setChannels(p => p.map(c => c.id === updated.id ? updated : c)), []);

  return (
    <div className="anim-in r-full" style={{ maxWidth: 900 }} data-testid="channels-page">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 32, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.2em", color: "#CC0000", marginBottom: 6 }}>MANAGE / SOURCES</div>
          <h1 style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "2rem", fontWeight: 700, color: "#111", margin: 0, lineHeight: 1 }}>CHANNELS</h1>
          <div style={{ height: 3, background: "#111", marginTop: 10, width: 64 }} />
        </div>
        <button data-testid="add-channel-btn" onClick={() => setShowForm(!showForm)} className="ind-btn-primary">
          {showForm ? "CANCEL ✕" : "+ ADD SOURCE"}
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="anim-in" style={{ marginBottom: 24 }}>
          <SectionHeader num="NEW" title="ADD SOURCE" />
          <div style={{ background: "#fff", border: "2px solid #111", padding: "24px", position: "relative" }}>
            <div style={{ position: "absolute", top: -3, left: -3, width: 6, height: 6, background: "#CC0000" }} />
            <div style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, background: "#CC0000" }} />
            <form onSubmit={handleAdd}>
              <div className="r-grid-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", letterSpacing: "0.16em", color: "#888", display: "block", marginBottom: 6 }}>CHANNEL NAME *</label>
                  <input data-testid="channel-name-input" type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="FIRESHIP" className="ind-input" />
                </div>
                <div>
                  <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", letterSpacing: "0.16em", color: "#888", display: "block", marginBottom: 6 }}>CHANNEL URL *</label>
                  <input data-testid="channel-url-input" type="url" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://youtube.com/@fireship" className="ind-input" />
                </div>
              </div>
              <div className="r-grid-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", letterSpacing: "0.16em", color: "#888", display: "block", marginBottom: 6 }}>YOUTUBE CHANNEL ID (UC...)</label>
                  <input data-testid="channel-id-input" type="text" value={form.channel_id} onChange={e => setForm(p => ({ ...p, channel_id: e.target.value }))} placeholder="UCxxxxxxxxxxxxxxxxxxxxxx" className="ind-input" />
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", color: "#bbb", marginTop: 4, letterSpacing: "0.06em" }}>Required for auto-summarize</p>
                </div>
                <div>
                  <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", letterSpacing: "0.16em", color: "#888", display: "block", marginBottom: 6 }}>HANDLE / NOTES</label>
                  <input data-testid="channel-handle-input" type="text" value={form.handle} onChange={e => setForm(p => ({ ...p, handle: e.target.value }))} placeholder="@fireship" className="ind-input" />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, paddingTop: 8, borderTop: "1px solid #e8e8e4" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1, minWidth: 240 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", letterSpacing: "0.14em", color: "#888" }}>NOTION DATABASE (FROM SETTINGS)</span>
                  <select data-testid="channel-db-select" value={form.notion_database_id} onChange={e => setForm(p => ({ ...p, notion_database_id: e.target.value }))} className="ind-input" style={{ fontSize: "0.7rem" }}>
                    <option value="">DEFAULT / NONE</option>
                    {notionDbs.map(d => <option key={d.database_id} value={d.database_id}>{(d.category || d.database_id).toUpperCase()}</option>)}
                  </select>
                  {notionDbs.length === 0 && (<span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", color: "#bbb", marginTop: 2 }}>ADD DATABASES IN SETTINGS → NOTION DATABASES FIRST</span>)}
                </div>
                <Toggle
                  checked={form.auto_summarize}
                  onChange={v => setForm(p => ({ ...p, auto_summarize: v }))}
                  label="ENABLE AUTO-SUMMARIZE"
                />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button data-testid="save-channel-btn" type="submit" disabled={adding} className="ind-btn-primary">
                  {adding ? <Loader2 size={13} className="animate-spin" /> : null}
                  SAVE SOURCE
                </button>
                <button data-testid="cancel-add-channel-btn" type="button" onClick={() => { setShowForm(false); setForm({ name: "", url: "", handle: "", channel_id: "", auto_summarize: false, notion_database_id: "" }); }} className="ind-btn-ghost">
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Channels table */}
      <SectionHeader num="01" title={`SOURCES · ${channels.length} INDEXED`} />
      <div style={{ border: "2px solid #111", background: "#fff" }}>
        {/* Header */}
        <div style={{ display: "grid", gridTemplateColumns: "44px 1fr auto auto auto", background: "#111" }}>
          {["#", "NAME / CHANNEL ID", "STATUS", "", ""].map((h, i) => (
            <div key={i} style={{ padding: "8px 12px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888", letterSpacing: "0.14em" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          [1, 2].map(i => (
            <div key={i} style={{ display: "flex", padding: "16px", borderBottom: "1px solid #eee", opacity: 0.4, gap: 16 }}>
              <div style={{ width: 32, height: 12, background: "#eee" }} />
              <div style={{ flex: 1, height: 12, background: "#eee" }} />
            </div>
          ))
        ) : channels.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center" }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", color: "#bbb", letterSpacing: "0.14em" }}>
              NO SOURCES · ADD A CHANNEL TO BEGIN
            </div>
          </div>
        ) : (
          channels.map((ch, i) => (
            <ChannelRow key={ch.id} ch={ch} index={i} onDelete={handleDelete} onUpdate={handleUpdate} />
          ))
        )}
      </div>

      {/* Help */}
      <div style={{ marginTop: 20, padding: "14px 18px", border: "1px solid #e8e8e4", background: "#fff" }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#888", letterSpacing: "0.1em", marginBottom: 6 }}>
          HOW TO FIND YOUR YOUTUBE CHANNEL ID
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#555", lineHeight: 1.7 }}>
          1. Go to your channel on YouTube<br />
          2. Click "More" → "About" → "Share channel" → "Copy channel ID"<br />
          3. Or visit: <a href="https://www.youtube.com/account_advanced" target="_blank" rel="noopener noreferrer" style={{ color: "#CC0000" }}>youtube.com/account_advanced</a><br />
          4. Or use: <a href="https://commentpicker.com/youtube-channel-id.php" target="_blank" rel="noopener noreferrer" style={{ color: "#CC0000" }}>commentpicker.com/youtube-channel-id.php</a>
        </div>
      </div>
    </div>
  );
}
